// Deck-feed card (DeckCard) display helpers — ADR-064 (Learn eligibility) and
// ADR-066 (first field of a repeating side-B kind is authoritative for Learn).

import type { Card, CardSide } from '../types'
import type { AppTranslations } from '../i18n/app'
import { hasDefinition, hasTranslation } from './cardFilters'
import { PLAIN_NOTE_LABEL, customSideName } from './cardSides'
import { valueSide, type ValueKind } from './fieldValue'

const VALUE_KINDS: ValueKind[] = ['translation', 'definition', 'example']
function isValueKind(kind: string | null | undefined): kind is ValueKind {
  return VALUE_KINDS.includes(kind as ValueKind)
}

type CardEditorLabels = AppTranslations['cardEditor']

/** The ADR-064 pair, present only when its content is blank. Order is the
 * order the not-in-study mark names them in. */
export type LearnGapField = 'translation' | 'definition'

export function learnGaps(card: Card): LearnGapField[] {
  const gaps: LearnGapField[] = []
  if (!hasTranslation(card)) gaps.push('translation')
  if (!hasDefinition(card)) gaps.push('definition')
  return gaps
}

/** ADR-064 Decision 1: a card enters Learn only with a non-blank Definition
 * or Translation side. Reuses the same content check the "not filled" chip
 * uses, so the not-in-study mark and the filter agree on what counts as
 * content — and the mark clears the instant either field gets content, by
 * whatever means it arrived (typed, Fill, import). */
export function isEligibleForLearn(card: Card): boolean {
  return learnGaps(card).length < 2
}

/** The single next field the not-in-study mark asks for: translation before
 * definition when neither is present yet, and whichever one remains once the
 * other has been added. `null` once both are present, when the mark stops
 * rendering entirely. Never reports both fields at once — a mark that stated
 * the whole pair in one line is what produced nested quotation marks and an
 * overlong line in Russian; the mark now advances one step at a time and
 * leaves the "why" to a separate, tap-only explanation. */
export function notInStudyKind(gaps: LearnGapField[]): LearnGapField | null {
  return gaps[0] ?? null
}

const KIND_DISPLAY_LABEL: Record<string, string> = {
  translation: 'Translation', definition: 'Definition', example: 'Example',
  note: 'Note', image: 'Image', audio: 'Audio', link: 'Link', dialog: 'Dialog',
}

/** Translated eyebrow label for a side kind. Falls back to the side's own
 * cosmetic label for kinds with no dedicated editor label (custom fields).
 *
 * ADR-040's display rule, stated in full: "display name = label if set, else
 * the kind name". Only the `note` kind can carry an authored name, so it is
 * the only case where the two halves differ — every note-kind side is a
 * Custom field and always renders the exact word its author typed, whatever
 * that word is, including one that happens to spell a built-in kind's name
 * ("Note", "Link", ...). `kind` alone decides which branch runs below; no
 * branch re-derives a kind, or a "is this really custom" verdict, from the
 * label text. The `|| ce.noteLabel` fallback exists only for the case stored
 * data cannot produce — a note-kind side with an empty label — never for a
 * note-kind side whose author happened to type the word "Note". */
export function sideKindLabel(
  side: { kind?: string | null; label: string },
  ce: CardEditorLabels,
): string {
  switch (side.kind) {
    case 'term': return ce.termLabel
    case 'translation': return ce.translationLabel
    case 'definition': return ce.definitionLabel
    case 'example': return ce.exampleLabel
    case 'note': return customSideName(side) || ce.noteLabel
    case 'image': return ce.imageLabel
    case 'audio': return ce.audioLabel
    case 'link': return ce.linkLabel
    case 'dialog': return ce.dialogLabel
    default: return side.label || KIND_DISPLAY_LABEL[side.kind ?? ''] || side.label
  }
}

/** The same translated name, for a field TYPE rather than a stored side —
 * brief §13's `+ Add Example` affordance, which names a field the card does not
 * have and so has no side to read. Routed through `sideKindLabel` rather than
 * duplicating the switch, so the affordance can never name a type differently
 * from the field it creates. */
export function fieldTypeLabel(
  type: { kind: string; customName: string | null },
  ce: CardEditorLabels,
): string {
  return sideKindLabel(
    { kind: type.kind, label: type.customName ?? PLAIN_NOTE_LABEL } as CardSide,
    ce,
  )
}

export interface DisplaySide {
  side: CardSide
  /** True when this kind has more than one field on this card (ADR-066 §2:
   * the STUDIED badge / subordination treatment applies only to kinds that
   * actually repeat — an ordinary single-field card stays unlabelled). */
  kindRepeats: boolean
  /** 1-based position of this field among sides sharing its kind, counted in
   * stored-position order. Names WHERE the field is ("2nd"); it no longer
   * decides which field is the card's (see `isValue`). */
  ordinalInKind: number
  /** This field is the card's value for its kind — the one Learn studies and
   * the STUDIED badge marks. For translation, definition and example it is the
   * first NON-BLANK field (ADR-104 Decisions 1 and 6), so a blank field is
   * never marked STUDIED. Other kinds are not governed by ADR-104 and keep the
   * first field (ADR-066 Decision 1). */
  isValue: boolean
}

/** Non-term sides in stored position order, each annotated with its rank
 * within its own kind. Order is never changed — ADR-066 Decision 4 forbids
 * any renderer sorting side-B fields by anything other than stored position;
 * subordination is expressed as styling on the naturally-ordered list, not
 * as a regrouping. */
export function displaySides(card: Card): DisplaySide[] {
  const sides = [...card.sides]
    .filter((s) => s.kind !== 'term')
    .sort((a, b) => a.position - b.position)

  const kindTotals = new Map<string, number>()
  for (const s of sides) {
    const key = s.kind ?? s.label
    kindTotals.set(key, (kindTotals.get(key) ?? 0) + 1)
  }

  const valueIds = new Set(
    VALUE_KINDS.map((k) => valueSide(sides, k)?.id).filter((id): id is string => !!id),
  )

  const kindSeen = new Map<string, number>()
  return sides.map((side) => {
    const key = side.kind ?? side.label
    const ordinalInKind = (kindSeen.get(key) ?? 0) + 1
    kindSeen.set(key, ordinalInKind)
    const isValue = isValueKind(side.kind) ? valueIds.has(side.id) : ordinalInKind === 1
    return { side, kindRepeats: (kindTotals.get(key) ?? 1) > 1, ordinalInKind, isValue }
  })
}

/** Kinds with more than one field, for the collapsed row's "2 EXAMPLES"
 * count — repeats are counted there, never shown (brief §5.1). */
export function repeatCounts(card: Card, ce: CardEditorLabels): { kind: string; label: string; count: number }[] {
  const grouped = displaySides(card)
  const byKind = new Map<string, { label: string; count: number }>()
  for (const { side, kindRepeats } of grouped) {
    if (!kindRepeats) continue
    const key = side.kind ?? side.label
    const existing = byKind.get(key)
    if (existing) existing.count += 1
    else byKind.set(key, { label: sideKindLabel(side, ce), count: 1 })
  }
  return Array.from(byKind.entries()).map(([kind, v]) => ({ kind, ...v }))
}
